import { LoadingSpinner } from "@/components/util/LoadingSpinner";
import { useCustomParkingVerbiageOnWeb as useCustomParkingVerbiage } from "@/hooks/useCustomParkingVerbiage";
import { RESERVE_TEXTS } from "@/lib/utils-reserve";
import { SaveReservationData } from "@/lib/utils-reserve";
import { Resource } from "@gettactic/api";
import { useMemo } from "react";
import { Controller, FieldErrors, UseFormReturn } from "react-hook-form";
import ReactSelect, { components } from "react-select";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue
} from "@cmp-src/primitives/Select";
import { useIsMobile } from "@v2/hooks/use-mobile";

type ReserveFieldParkingListProps = {
  form: UseFormReturn<SaveReservationData>;
  parkingResourcesList: Resource[] | null | undefined;
  defaultValue: Resource | null;
  noParkingResource?: Resource;
  errors: FieldErrors<SaveReservationData>;
};

export default function ReserveFieldParkingList({
  parkingResourcesList,
  form,
  defaultValue: def,
  noParkingResource,
  errors
}: ReserveFieldParkingListProps) {
  const isMobile = useIsMobile();

  const parkingList = useMemo(() => {
    if (!parkingResourcesList) {
      return null;
    }
    if (noParkingResource) {
      return parkingResourcesList.concat(noParkingResource);
    }
    return parkingResourcesList;
  }, [noParkingResource, parkingResourcesList]);

  const options = useMemo(
    () =>
      (parkingList ?? []).map((res) => ({
        ...res,
        label: res.name,
        value: res.id
      })),
    [parkingList]
  );
  const defaultValue = useMemo(
    () => options.find((res) => res.id === def?.id),
    [def, options]
  );
  const customParkingVerbiage = useCustomParkingVerbiage();

  if (!parkingList) {
    return (
      <LoadingSpinner color="text-secondary [.tactic-v2_&]:text-secondary-foreground" />
    );
  }

  const placeholder = customParkingVerbiage
    ? `Select ${customParkingVerbiage}`
    : RESERVE_TEXTS.selectParking;

  return (
    <>
      <Controller
        control={form.control}
        name={"parking_resource"}
        rules={{ required: true }}
        defaultValue={defaultValue}
        render={({ field: { onChange, value } }) =>
          isMobile ? (
            <Select
              value={value?.id}
              onValueChange={(value) => {
                onChange({ value: options.find((res) => res.id === value) });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options?.map((option) => (
                  <SelectItem
                    key={option.id}
                    value={option.id}
                    disabled={option.type === "area"}
                  >
                    {option.name}
                    {option.available_slots > 0 && (
                      <span className="ml-2 rounded-full bg-green-100 px-2.5 py-0.5 font-medium text-green-800 text-xs">
                        {option.available_slots}
                      </span>
                    )}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <ReactSelect
              menuPortalTarget={document.body}
              menuPosition={"fixed"}
              onChange={onChange}
              value={value}
              placeholder={placeholder}
              className={"resource-select"}
              classNamePrefix="react-select"
              isOptionDisabled={(option) => option.type === "area"}
              noOptionsMessage={() => "No availability"}
              components={{
                GroupHeading: (props) => (
                  <div className="mx-2 h-8 border-gray-50 border-b-2 pl-2 font-bold">
                    {props.data.label}
                  </div>
                ),
                Option: (props) => (
                  <components.Option {...props}>
                    {props.data.name}
                    {props.data.available_slots > 0 && (
                      <span className="ml-2 rounded-full bg-green-100 px-2.5 py-0.5 font-medium text-green-800 text-xs">
                        {props.data.available_slots}
                      </span>
                    )}
                  </components.Option>
                )
              }}
              options={options}
            />
          )
        }
      />
      {errors?.parking_resource &&
        errors.parking_resource.type === "required" && (
          <p className="text-primary">{placeholder}</p>
        )}
    </>
  );
}
